import { Check, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTheme, useTextTheme } from '../../theme/hooks'

type PasswordStrengthProps = {
  password: string
  onStrengthChange?: (strength: number) => void
}

type PasswordCriteria = {
  label: string
  test: (password: string) => boolean
  weight: number
}

const PasswordStrength = ({
  password,
  onStrengthChange,
}: PasswordStrengthProps) => {
  const { theme } = useTheme()
  const textTheme = useTextTheme()

  const [criteria, setCriteria] = useState<PasswordCriteria[]>([
    {
      label: 'Au moins 8 caractères',
      test: pwd => pwd.length >= 8,
      weight: 1,
    },
    {
      label: 'Au moins une majuscule',
      test: pwd => /[A-Z]/.test(pwd),
      weight: 1,
    },
    {
      label: 'Au moins une minuscule',
      test: pwd => /[a-z]/.test(pwd),
      weight: 1,
    },
    {
      label: 'Au moins un chiffre',
      test: pwd => /\d/.test(pwd),
      weight: 1,
    },
    {
      label: 'Au moins un caractère spécial',
      test: pwd => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
      weight: 1,
    },
    {
      label: 'Au moins 12 caractères',
      test: pwd => pwd.length >= 12,
      weight: 2,
    },
  ])

  const [strength, setStrength] = useState(0)
  const [maxStrength] = useState(criteria.reduce((sum, c) => sum + c.weight, 0))

  useEffect(() => {
    if (!password) {
      setStrength(0)
      onStrengthChange?.(0)
      return
    }

    const passedCriteria = criteria.filter(c => c.test(password))
    const currentStrength = passedCriteria.reduce((sum, c) => sum + c.weight, 0)

    setStrength(currentStrength)
    onStrengthChange?.(currentStrength)
  }, [password, criteria, onStrengthChange])

  const getStrengthLabel = (strength: number) => {
    const percentage = (strength / maxStrength) * 100

    if (percentage < 30) return { label: 'Très faible', color: theme.status.error.icon, bgColor: theme.status.error.bg }
    if (percentage < 50) return { label: 'Faible', color: theme.status.warning.icon, bgColor: theme.status.warning.bg }
    if (percentage < 70) return { label: 'Moyen', color: theme.status.warning.text, bgColor: theme.status.warning.bg }
    if (percentage < 90) return { label: 'Fort', color: theme.status.info.icon, bgColor: theme.status.info.bg }
    return { label: 'Très fort', color: theme.status.success.icon, bgColor: theme.status.success.bg }
  }

  const strengthInfo = getStrengthLabel(strength)

  if (!password) return null

  return (
    <div className="mt-3 space-y-3">
      {/* strength bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: textTheme.tertiary }}>Force du mot de passe</span>
          <span className="font-medium" style={{ color: strengthInfo.color }}>
            {strengthInfo.label}
          </span>
        </div>
        <div
          className="h-2 overflow-hidden rounded-full"
          style={{ backgroundColor: theme.background.tertiary }}
        >
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${(strength / maxStrength) * 100}%`,
              backgroundColor: strengthInfo.color,
            }}
          />
        </div>
      </div>

      {/* criteria checklist */}
      <div className="space-y-2">
        {criteria.map((criterion, index) => {
          const isPassed = criterion.test(password)
          return (
            <div
              key={index}
              className="flex items-center space-x-2 text-sm transition-colors duration-200"
              style={{ color: isPassed ? theme.status.success.text : textTheme.tertiary }}
            >
              {isPassed ? (
                <Check className="h-4 w-4" style={{ color: theme.status.success.icon }} />
              ) : (
                <X className="h-4 w-4" style={{ color: textTheme.disabled }} />
              )}
              <span>{criterion.label}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PasswordStrength
